import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, TrendingUp, Filter } from "lucide-react";
import { api, fmtAum, fmtNav, fmtPct, cls } from "@/lib/api";
import StatCard from "@/components/StatCard";
import ContactModal from "@/components/ContactModal";
import { APP_NAME } from "@/constants/app";

export default function FundList() {
  const [funds, setFunds] = useState([]);
  const [overview, setOverview] = useState(null);
  const [options, setOptions] = useState({ categories: [], amcs: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [amc, setAmc] = useState("");
  const [sort, setSort] = useState("aum_cr");
  const [contactModalOpen, setContactModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const [ov, opt] = await Promise.all([
        api.get("/stats/overview"),
        api.get("/funds/filters/options"),
      ]);
      setOverview(ov.data);
      setOptions(opt.data);
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { sort, order: "desc" };
    if (q) params.q = q;
    if (category) params.category = category;
    if (amc) params.amc = amc;
    const t = setTimeout(() => {
      api.get("/funds", { params }).then((r) => {
        setFunds(r.data);
        setLoading(false);
      });
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q, category, amc, sort]);

  const bestPerformer = overview?.top_performer_1m;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
      {/* Hero */}
      <section data-testid="hero-section" className="mb-6 sm:mb-8">
        <div className="flex items-center gap-2 text-xs font-semibold text-sif-secondary uppercase tracking-widest">
          <TrendingUp className="w-3.5 h-3.5" /> Live {APP_NAME}
        </div>
        <h1 className="mt-2 sm:mt-3 text-2xl sm:text-4xl md:text-5xl font-bold text-sif-primary leading-tight tracking-tight">
          Track India's Specialized <br className="hidden sm:inline" /> Investment Funds — deeply.
        </h1>
        <p className="mt-3 sm:mt-4 text-sif-muted max-w-2xl text-sm sm:text-[15px] leading-relaxed">
          Real-time NAV, rolling returns, and performance analytics across every SIF in India — one clean, opinionated dashboard.
        </p>
      </section>

      {/* Overview stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <StatCard
          testId="stat-total-funds"
          label="Live SIFs"
          value={overview?.total_funds ?? "…"}
          sublabel="Across active AMCs"
        />
        <StatCard
          testId="stat-total-aum"
          label="Total SIF AUM"
          value={overview ? fmtAum(overview.total_aum_cr) : "…"}
          sublabel={overview?.aum_date ? `As of ${overview.aum_date}` : "AMFI Disclosures (₹ Cr)"}
          accent="text-sif-secondary"
        />
        <StatCard
          testId="stat-total-amcs"
          label="Active AMCs"
          value={overview?.total_amcs ?? "…"}
          sublabel="Fund houses"
        />
        <StatCard
          testId="stat-top-performer"
          label="Top Performer · 1M"
          value={bestPerformer ? fmtPct(bestPerformer.return_1m) : "…"}
          sublabel={bestPerformer?.name?.split("Long-Short")[0] || "—"}
          accent="text-sif-accent"
        />
      </section>

      {/* Filters */}
      <section className="bg-white rounded-xl border border-sif p-3.5 sm:p-4 card-shadow mb-6">
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sif-muted" />
            <input
              data-testid="fund-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search fund or AMC…"
              className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-sif-primary/20 focus:bg-white transition"
            />
          </div>

          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2.5 sm:gap-3">
            <select
              data-testid="filter-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full sm:w-auto px-3 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm text-sif-text"
            >
              <option value="">All Categories</option>
              {options.categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            <select
              data-testid="filter-amc"
              value={amc}
              onChange={(e) => setAmc(e.target.value)}
              className="w-full sm:w-auto px-3 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm text-sif-text"
            >
              <option value="">All AMCs</option>
              {options.amcs.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>

            <select
              data-testid="filter-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="col-span-2 sm:col-span-1 w-full sm:w-auto px-3 py-2.5 rounded-lg border border-sif bg-slate-50 text-sm text-sif-text"
            >
              <option value="aum_cr">Sort: AUM</option>
              <option value="name">Sort: Name</option>
              <option value="latest_nav">Sort: NAV</option>
            </select>
          </div>
        </div>
      </section>

      {/* Fund table */}
      <section
        data-testid="fund-table-section"
        className="bg-white rounded-xl border border-sif card-shadow overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] table-auto">
            <thead className="bg-slate-50 border-b border-sif">
              <tr className="text-left text-[11px] uppercase tracking-wider text-sif-muted">
                <th className="px-3.5 sm:px-5 py-3 font-semibold w-auto min-w-[220px]">Fund</th>
                <th className="px-2 sm:px-3 py-3 font-semibold w-[12%] min-w-[90px]">Category</th>
                <th className="px-2 sm:px-3 py-3 font-semibold text-right w-[10%] min-w-[70px]">NAV</th>
                <th className="px-2 sm:px-3 py-3 font-semibold text-right w-[8%] min-w-[60px]">1D</th>
                <th className="px-2 sm:px-3 py-3 font-semibold text-right w-[8%] min-w-[60px]">1M</th>
                <th className="px-2 sm:px-3 py-3 font-semibold text-right w-[8%] min-w-[60px]">3M</th>
                <th className="px-2 sm:px-3 py-3 font-semibold text-right w-[8%] min-w-[60px]">1Y</th>
                <th className="px-2.5 sm:px-4 py-3 font-semibold text-right w-[10%] min-w-[75px]">AUM</th>
                <th className="px-2.5 sm:px-4 py-3 font-semibold text-center w-[12%] min-w-[100px]">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="text-center py-16 text-sif-muted">Loading funds…</td>
                </tr>
              )}
              {!loading && funds.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-16 text-sif-muted">No funds match your filters.</td>
                </tr>
              )}
              {!loading &&
                funds.map((f) => (
                  <tr
                    key={f.code}
                    data-testid={`fund-row-${f.code}`}
                    className="border-b border-sif last:border-0 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-3.5 sm:px-5 py-3 sm:py-3.5">
                      <Link
                        to={`/fund/${f.code}`}
                        data-testid={`fund-link-${f.code}`}
                        className="block group"
                      >
                        <div className="font-semibold text-sif-primary text-xs sm:text-sm group-hover:text-sif-secondary transition-colors leading-snug">
                          {f.name}
                        </div>
                        <div className="text-[11px] sm:text-xs text-sif-muted mt-0.5">{f.amc}</div>
                      </Link>
                    </td>
                    <td className="px-2 sm:px-3 py-3 sm:py-3.5 whitespace-nowrap">
                      <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-[10px] sm:text-[11px] font-medium text-sif-text truncate max-w-[110px]">
                        {f.category}
                      </span>
                    </td>
                    <td className="px-2 sm:px-3 py-3 sm:py-3.5 text-right font-mono font-semibold text-xs sm:text-sm text-sif-primary whitespace-nowrap">
                      {fmtNav(f.latest_nav)}
                    </td>
                    <td className={`px-2 sm:px-3 py-3 sm:py-3.5 text-right font-mono text-xs sm:text-sm whitespace-nowrap ${cls(f.day_change_pct)}`}>
                      {fmtPct(f.day_change_pct)}
                    </td>
                    <td className={`px-2 sm:px-3 py-3 sm:py-3.5 text-right font-mono text-xs sm:text-sm whitespace-nowrap ${cls(f.return_1m)}`}>
                      {fmtPct(f.return_1m)}
                    </td>
                    <td className={`px-2 sm:px-3 py-3 sm:py-3.5 text-right font-mono text-xs sm:text-sm whitespace-nowrap ${cls(f.return_3m)}`}>
                      {fmtPct(f.return_3m)}
                    </td>
                    <td className={`px-2 sm:px-3 py-3 sm:py-3.5 text-right font-mono text-xs sm:text-sm whitespace-nowrap ${cls(f.return_1y)}`}>
                      {fmtPct(f.return_1y)}
                    </td>
                    <td className="px-2.5 sm:px-4 py-3 sm:py-3.5 text-right text-xs sm:text-sm text-sif-text whitespace-nowrap">
                      {fmtAum(f.aum_cr)}
                    </td>
                    <td className="px-2.5 sm:px-4 py-3 sm:py-3.5 text-center whitespace-nowrap">
                      <button
                        onClick={() => setContactModalOpen(true)}
                        className="px-3 py-1.5 bg-sif-primary hover:bg-opacity-90 text-white rounded text-[11px] sm:text-xs font-bold shadow-sm hover:shadow transition duration-150"
                      >
                        Invest Now
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-xs text-sif-muted mt-4 flex items-center gap-1.5">
        <Filter className="w-3 h-3 flex-shrink-0" /> Data seeded from public SIF disclosures · Rolling returns computed daily.
      </p>

      <ContactModal open={contactModalOpen} onOpenChange={setContactModalOpen} />
    </div>
  );
}
